/**
 * Empty-state card when ITMS is down or not configured for this ROS environment.
 */
import { ArrowsClockwise, Plugs } from "@phosphor-icons/react";

export function parseHelpdeskDetail(body, fallback) {
  const d = body && body.detail;
  if (typeof d === "string" && d.trim()) return d.trim();
  if (Array.isArray(d)) {
    const parts = d.map((item) => {
      if (typeof item === "string") return item;
      if (item && item.msg) return item.msg;
      return "";
    }).filter(Boolean);
    if (parts.length) return parts.join("; ");
  }
  if (d && typeof d === "object") {
    const parts = Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`);
    if (parts.length) return parts.join("; ");
  }
  return fallback;
}

export async function readHelpdeskError(res, fallback) {
  const body = await res.json().catch(() => ({}));
  return { message: parseHelpdeskDetail(body, fallback), status: res.status };
}

function normalizeError(error) {
  if (!error) return { message: "", status: undefined };
  if (typeof error === "string") return { message: error, status: undefined };
  return { message: error.message || String(error), status: error.status };
}

export function isHelpdeskUnavailable(error) {
  const { message, status } = normalizeError(error);
  if (status === 502 || status === 503 || status === 504) return true;
  const m = (message || "").toLowerCase();
  return (
    m.includes("itms is unreachable")
    || m.includes("temporarily unavailable")
    || m.includes("not configured")
    || m.includes("could not reach helpdesk")
  );
}

export default function HelpdeskUnavailable({ error, onRetry, compact = false }) {
  const { message, status } = normalizeError(error);
  const unconfigured = (message || "").toLowerCase().includes("not configured") || status === 503;
  const title = unconfigured
    ? "Helpdesk is not connected"
    : "IT helpdesk is unavailable";
  const body = unconfigured
    ? "This ROS environment is not connected to the IT ticket system (ITMS). You can keep using the rest of ROS; tickets cannot be loaded or submitted until IT reconnects it."
    : "Tickets live in ITMS, and we could not reach it just now. Try again in a moment. The rest of ROS still works.";

  if (compact) {
    return (
      <div
        role="status"
        className="mb-4 rounded-xl border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/40 p-4"
      >
        <div className="flex gap-3">
          <div className="w-9 h-9 shrink-0 rounded-lg bg-white dark:bg-slate-900 border border-amber-200 dark:border-amber-900/60 flex items-center justify-center">
            <Plugs size={18} className="text-amber-700 dark:text-amber-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-neutral-800 dark:text-white">{title}</p>
            <p className="text-xs text-neutral-600 dark:text-slate-300 mt-1">{body}</p>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border border-amber-300 dark:border-amber-800 text-amber-900 dark:text-amber-200 hover:bg-white dark:hover:bg-slate-900"
              >
                <ArrowsClockwise size={12} />
                Try again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      className="rounded-xl border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/40 p-10 text-center"
    >
      <div className="w-12 h-12 rounded-xl bg-white dark:bg-slate-900 border border-amber-200 dark:border-amber-900/60 flex items-center justify-center mx-auto mb-3">
        <Plugs size={22} className="text-amber-700 dark:text-amber-400" />
      </div>
      <p className="text-sm font-semibold text-neutral-800 dark:text-white">{title}</p>
      <p className="text-sm text-neutral-600 dark:text-slate-300 mt-2 max-w-md mx-auto">{body}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-amber-300 dark:border-amber-800 bg-white dark:bg-slate-900 text-amber-900 dark:text-amber-200 hover:bg-amber-100/80 dark:hover:bg-amber-950/60"
        >
          <ArrowsClockwise size={14} />
          Try again
        </button>
      )}
    </div>
  );
}
